import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Activity, CalendarDays, CheckCircle2, Circle, Clock, XCircle, Truck, Building2, Users, UserCog } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [rides, setRides] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/rides").then((r) => setRides(r.data.slice(0, 8)));
  }, []);

  const kpis = [
    { key: "today_rides", label: "Rides Today", icon: CalendarDays, tint: "text-[#005E82]" },
    { key: "pending", label: "Pending", icon: Clock, tint: "text-amber-600" },
    { key: "in_progress", label: "In Progress", icon: Activity, tint: "text-sky-600" },
    { key: "completed", label: "Completed", icon: CheckCircle2, tint: "text-emerald-600" },
    { key: "canceled", label: "Canceled", icon: XCircle, tint: "text-rose-600" },
    { key: "total_rides", label: "Total Rides", icon: CalendarDays, tint: "text-slate-700" },
  ];

  const fleet =
    user?.role !== "client_user"
      ? [
          { key: "companies", label: "Client Companies", icon: Building2 },
          { key: "vehicles", label: "Vehicles", icon: Truck },
          { key: "drivers", label: "Drivers", icon: UserCog },
          { key: "customers", label: "Customers", icon: Users },
        ]
      : [];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <header>
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Overview</div>
        <h1 className="font-display text-3xl font-bold text-[#0A192F] mt-1">Welcome back, {user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-slate-600 mt-1">Here's how your operations look right now.</p>
      </header>

      {(user?.role === "agency_admin" || user?.role === "dispatcher") && <OnboardingCard user={user} />}

      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((k) => (
          <div key={k.key} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`kpi-${k.key}`}>
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">{k.label}</div>
              <k.icon size={16} className={k.tint} strokeWidth={1.75} />
            </div>
            <div className="mt-3 font-display text-2xl font-bold text-[#0A192F]">
              {stats ? stats[k.key] ?? 0 : "—"}
            </div>
          </div>
        ))}
      </section>

      {fleet.length > 0 && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {fleet.map((f) => (
            <div key={f.key} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`fleet-${f.key}`}>
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">{f.label}</div>
                <f.icon size={16} className="text-[#005E82]" strokeWidth={1.75} />
              </div>
              <div className="mt-3 font-display text-2xl font-bold text-[#0A192F]">{stats ? stats[f.key] ?? 0 : "—"}</div>
            </div>
          ))}
        </section>
      )}

      <section className="bg-white border border-slate-200 rounded-md">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-slate-900">Upcoming rides</h3>
            <div className="text-xs text-slate-500 mt-0.5">Nearest pickups first</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-[0.1em] text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2">Patient</th>
                <th className="text-left px-4 py-2">Pickup</th>
                <th className="text-left px-4 py-2">Time</th>
                <th className="text-left px-4 py-2">Mobility</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rides.length === 0 && (
                <tr><td colSpan={5}><div className="empty">No rides yet.</div></td></tr>
              )}
              {rides.map((r) => (
                <tr key={r.id} className="tbl-row border-t border-slate-100">
                  <td className="px-4 py-2.5">{r.patient_name}</td>
                  <td className="px-4 py-2.5 text-slate-600 truncate max-w-[280px]">{r.pickup_address}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.pickup_datetime}</td>
                  <td className="px-4 py-2.5 capitalize text-slate-600">{r.mobility}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function OnboardingCard({ user }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    api
      .get("/agency/onboarding")
      .then((r) => setData(r.data))
      .catch(() => setFailed(true));
  }, []);

  const hide = () => {
    setDismissed(true);
    api.post("/agency/onboarding/dismiss").catch(() => {});
  };

  if (failed || !data || dismissed || data.dismissed_at) return null;

  const trial = user?.subscription?.status === "trial" ? user.subscription : null;

  if (data.complete) {
    return (
      <section className="bg-white border border-slate-200 rounded-md px-5 py-3.5 flex items-center justify-between" data-testid="onboarding-card">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} className="text-emerald-600" strokeWidth={1.75} />
          <div className="text-sm font-medium text-slate-900">All set. You've finished onboarding.</div>
        </div>
        <button onClick={hide} className="text-xs text-slate-500 hover:text-slate-900" data-testid="onboarding-dismiss">
          Hide
        </button>
      </section>
    );
  }

  const pct = data.required_total > 0 ? Math.round((data.required_done / data.required_total) * 100) : 0;

  return (
    <section className="bg-white border border-slate-200 rounded-md" data-testid="onboarding-card">
      <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-slate-900">Getting started</h3>
          <div className="text-xs text-slate-500 mt-0.5">
            {data.required_done} of {data.required_total} done
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2 max-w-xs">
            <div className="h-full bg-[#005E82]" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <button onClick={hide} className="text-xs text-slate-500 hover:text-slate-900 hover:underline flex-shrink-0" data-testid="onboarding-dismiss">
          Hide this
        </button>
      </div>
      <ul className="divide-y divide-slate-100">
        {data.steps.map((step) => (
          <li key={step.key} className="px-5 py-3 flex items-start gap-3" data-testid={`onboarding-step-${step.key}`}>
            {step.done ? (
              <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
            ) : (
              <Circle size={16} className="text-slate-300 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={step.to} className="text-sm font-medium text-[#005E82] hover:underline">
                  {step.title}
                </Link>
                {step.optional && <span className="text-[10px] uppercase tracking-wide text-slate-400">(optional)</span>}
                {typeof step.count === "number" && step.count > 0 && (
                  <span className="text-[11px] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{step.count}</span>
                )}
              </div>
              {step.hint && <div className="text-xs text-slate-500 mt-0.5">{step.hint}</div>}
            </div>
          </li>
        ))}
      </ul>
      {trial && typeof trial.days_left === "number" && (
        <div className="px-5 py-3 border-t border-slate-200 text-xs text-slate-500">
          Your free trial has {trial.days_left} day{trial.days_left === 1 ? "" : "s"} left.{" "}
          <Link to="/app/subscription" className="text-[#005E82] font-medium hover:underline">
            Plan details
          </Link>
        </div>
      )}
    </section>
  );
}

export function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    assigned: "bg-sky-50 text-sky-800 border-sky-200",
    in_progress: "bg-indigo-50 text-indigo-800 border-indigo-200",
    completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
    canceled: "bg-rose-50 text-rose-800 border-rose-200",
  };
  const cls = map[status] || "bg-slate-50 text-slate-700 border-slate-200";
  return <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 border ${cls} capitalize`}>{status?.replace("_", " ")}</span>;
}
